import {
  DayOfWeek,
  daysOfWeekValues,
  StringHHColonmm,
  StringHHmm,
  StringYYYYMMDD,
  StringYYYYMMDDHHmm,
  StringYYYYMMDDWithDashes,
} from '../models/date-time';
import { toStringHHmm, toStringYYYYMMDD, toStringYYYYMMDDWithDashes } from './date-type-functions';
import { AppConstants } from '../config/app-constants';

export class DateUtils {
  public static twoMonthsInDays = 60;
  public static oneYearInSeconds = 31536000; // 60 * 60 * 24 * 365
  public static twoYearsInSeconds = DateUtils.oneYearInSeconds * 2;

  // returns 'YYYYMMDD'
  public static dateNow(): StringYYYYMMDD {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return toStringYYYYMMDD(`${year}${month}${day}`);
  }

  // 'YYYY-MM-DD' => 'YYYYMMDD'
  public static removeDashFromDateString(dateWithDash: StringYYYYMMDDWithDashes): StringYYYYMMDD {
    return toStringYYYYMMDD(dateWithDash.replace(/-/g, ''));
  }

  // HH:mm => HHmm
  public static removeColonFromTimeString(timeWithColon: StringHHColonmm): StringHHmm {
    return toStringHHmm(timeWithColon.replace(/:/g, ''));
  }

  // HHmm => HH:mm
  public static addColonToTimeString(time: StringHHmm): StringHHColonmm {
    return `${time.substring(0, 2)}:${time.substring(2, 4)}` as StringHHColonmm;
  }

  // 'YYYYMMDD' => 'YYYY-MM-DD'
  public static addDashToStringYYYYMMDD(date: StringYYYYMMDD): StringYYYYMMDDWithDashes {
    return toStringYYYYMMDDWithDashes(`${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}`);
  }

  // 'YYYYMMDD' => Date
  public static parseYYYYMMDD(dateString: StringYYYYMMDD): Date {
    const year = parseInt(dateString.substring(0, 4), 10);
    const month = parseInt(dateString.substring(4, 6), 10) - 1; // Month is 0-indexed
    const day = parseInt(dateString.substring(6, 8), 10);
    return new Date(year, month, day);
  }

  // Date => locale-specific short date string (e.g. '01.03.2026' in de, '3/1/2026' in en)
  public static formatDateLocale(date: Date, locale?: string, dateStyle: 'short' | 'medium' | 'long' | 'full' = 'short'): string {
    return new Intl.DateTimeFormat(locale ?? AppConstants.defaultLocale, { dateStyle }).format(date);
  }

  public static convertMsSinceEpochToDate(msSinceEpoch = 0): Date {
    return new Date(msSinceEpoch);
  }

  public static formatDateLocaleFromYYYYMMDDHHmm(
    stringYYYYMMDDHHmm: StringYYYYMMDDHHmm,
    locale?: string,
    dateStyle: 'short' | 'medium' | 'long' | 'full' = 'short',
  ): string {
    const datePart = DateUtils.getDatePartYYYYMMDD(stringYYYYMMDDHHmm);
    const date = DateUtils.parseYYYYMMDD(datePart);
    const hours = parseInt(stringYYYYMMDDHHmm.substring(8, 10), 10);
    const minutes = parseInt(stringYYYYMMDDHHmm.substring(10, 12), 10);
    date.setHours(hours, minutes, 0, 0);
    return DateUtils.formatDateLocale(date, locale, dateStyle);
  }

  public static formatDateLocaleFromYYYYMMDD(
    stringYYYYMMDD: StringYYYYMMDD,
    locale?: string,
    dateStyle: 'short' | 'medium' | 'long' | 'full' = 'short',
  ): string {
    const date = DateUtils.parseYYYYMMDD(stringYYYYMMDD);
    return DateUtils.formatDateLocale(date, locale, dateStyle);
  }

  // returns 'YYYY-MM-DD'
  public static formatDate(date: Date): StringYYYYMMDDWithDashes {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return toStringYYYYMMDDWithDashes(`${year}-${month}-${day}`);
  }

  // returns 'YYYYMMDD'
  public static formatDateYYYYMMDD(date: Date): StringYYYYMMDD {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return toStringYYYYMMDD(`${year}${month}${day}`);
  }

  public static getISOWeek(date: Date): { year: number; week: number } {
    const target = new Date(date.valueOf());
    const dayNr = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
    }
    const weekNumber = 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
    return { year: target.getFullYear(), week: weekNumber };
  }

  public static getWeekStartDate(date: Date, weekStartDay = 1): Date {
    const result = new Date(date);
    const day = result.getDay();
    const diff = (day < weekStartDay ? 7 : 0) + day - weekStartDay;
    result.setDate(result.getDate() - diff);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  public static getDayOfWeekFromDate(date: Date): DayOfWeek {
    const dayIndex = (date.getDay() + 6) % 7;
    return daysOfWeekValues[dayIndex];
  }

  public static getWeeklyDates(startDate: Date): Date[] {
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      dates.push(DateUtils.addDays(startDate, i));
    }
    return dates;
  }

  public static getWeekEndDate(weekStart: Date): Date {
    const result = new Date(weekStart);
    result.setDate(result.getDate() + 6);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  public static addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  public static addDaysInMs(msSinceEpoch: number, days: number): number {
    return msSinceEpoch + days * 24 * 60 * 60 * 1000;
  }

  public static nextDays(baseDate: Date, daysToAdd: number): Date[] {
    const dates: Date[] = [];
    for (let i = 1; i <= daysToAdd; i++) {
      dates.push(DateUtils.addDays(baseDate, i));
    }
    return dates;
  }

  public static previousDays(baseDate: Date, daysToSubtract: number): Date[] {
    const dates: Date[] = [];
    for (let i = daysToSubtract; i >= 1; i--) {
      dates.push(DateUtils.addDays(baseDate, -i));
    }
    return dates;
  }

  // 'YYYYMMDDHHmm' => 'HHmm'
  public static getTimePartHHmm(dateSlot: StringYYYYMMDDHHmm): StringHHmm {
    return toStringHHmm(`${dateSlot.substring(8, 12)}`);
  }

  // 'YYYYMMDDHHmm' => 'YYYYMMDD'
  public static getDatePartYYYYMMDD(dateSlot: StringYYYYMMDDHHmm): StringYYYYMMDD {
    return toStringYYYYMMDD(`${dateSlot.substring(0, 8)}`);
  }

  // 'YYYYMMDD', 'HHmm' => 'YYYYMMDDHHmm'
  public static assembleYYYYMMDDHHmm(date: StringYYYYMMDD, time: StringHHmm): StringYYYYMMDDHHmm {
    return `${date}${time}` as StringYYYYMMDDHHmm;
  }

  // returns the ordinal of the calendar week
  public static getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  public static isDateOlderThanYesterday(date: StringYYYYMMDD): boolean {
    const yesterday = DateUtils.addDays(new Date(), -1);
    return DateUtils.parseYYYYMMDD(date) < yesterday;
  }

  public static isDateTooFarInAdvance(date: StringYYYYMMDD, bookableInAdvanceMonths: number): boolean {
    const limit = new Date();
    limit.setMonth(limit.getMonth() + bookableInAdvanceMonths);
    return DateUtils.parseYYYYMMDD(date) > limit;
  }

  public static getNumOfDaysUntil(msSinceEpochUntil: number): number {
    if (!msSinceEpochUntil) return 0;
    const diff = msSinceEpochUntil - Date.now();
    return Math.max(0, Math.floor(diff / AppConstants.oneDayInMs));
  }

  public static secondsToMs(value?: number | null | undefined): number | undefined {
    return value === null || value === undefined ? undefined : value * 1000;
  }
}
